import React, { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { FixedSizeList as List } from "react-window";

import { Box, Stack } from "@mui/material";
import { IFolder } from "store/types";

import { useDealWorkspace } from "../context/DealWorkspace-context";
import { Folder, ROW_HEIGHT } from "./Folder";

function getVisibleFolderRows(
  folders: IFolder[],
  openedFolderIds: Set<number>,
  getRootFolders: (folders: IFolder[]) => IFolder[],
  getChildFolders: (parentId: number, folders?: IFolder[]) => IFolder[]
): Array<{ folder: IFolder; depth: number }> {
  const rows: Array<{ folder: IFolder; depth: number }> = [];

  const walk = (folderList: IFolder[], depth: number) => {
    for (const folder of folderList) {
      rows.push({ folder, depth });
      if (openedFolderIds.has(folder.id)) {
        walk(getChildFolders(folder.id, folders), depth + 1);
      }
    }
  };

  walk(getRootFolders(folders), 0);
  return rows;
}

interface VirtualizedFolderListProps {
  maxHeight: number;
  folderList: IFolder[];
  isCloudMirrorFolder?: boolean;
}

export const VirtualizedFolderList: React.FC<VirtualizedFolderListProps> = ({
  maxHeight,
  folderList,
  isCloudMirrorFolder
}) => {
  const listRef = useRef<List>(null);
  const lastScrolledToFolderIdRef = useRef<number | null>(null);
  const { openedFolderIds, getChildFolders, getRootFolders, currentFolder } = useDealWorkspace();

  // ------------------------virtualized folder list------------------------
  const visibleRows = useMemo(
    () => getVisibleFolderRows(folderList, openedFolderIds, getRootFolders, getChildFolders),
    [folderList, openedFolderIds, getRootFolders, getChildFolders]
  );

  const contentHeight = visibleRows.length * ROW_HEIGHT;
  const listHeight = Math.max(1, Math.min(maxHeight, contentHeight));

  // Track which folder IDs are newly visible (children just expanded into view)
  // so we only animate those — not items that were already visible and just shifted positions
  const prevVisibleIdsRef = useRef<Set<number>>(new Set());
  const newlyVisibleIds = useMemo(() => {
    const prev = prevVisibleIdsRef.current;
    return new Set(visibleRows.filter((r) => !prev.has(r.folder.id)).map((r) => r.folder.id));
  }, [visibleRows]);

  useEffect(() => {
    prevVisibleIdsRef.current = new Set(visibleRows.map((r) => r.folder.id));
  }, [visibleRows]);

  // Scroll to selected folder only when selection changes (navigation) or panel first opens, not when expanding with arrow
  useLayoutEffect(() => {
    if (!currentFolder || visibleRows.length === 0 || !listRef.current) return;
    if (lastScrolledToFolderIdRef.current === currentFolder.id) return;
    const index = visibleRows.findIndex((row) => row.folder.id === currentFolder.id);
    if (index >= 0) {
      listRef.current.scrollToItem(index, "smart");
      lastScrolledToFolderIdRef.current = currentFolder.id;
    }
  }, [currentFolder?.id, visibleRows]);

  // Reset when selected folder changes (so we scroll again when it appears in the list)
  useLayoutEffect(() => {
    if (!currentFolder) lastScrolledToFolderIdRef.current = null;
  }, [currentFolder?.id]);

  if (!visibleRows.length) {
    return null;
  }

  return (
    <Stack
      sx={{
        width: "100%",
        paddingLeft: isCloudMirrorFolder ? "0" : "25px",
        height: listHeight,
        minHeight: 0,
        flexShrink: 0,
        transition: "height 0.2s ease"
      }}
    >
      <List
        ref={listRef}
        height={listHeight}
        width="100%"
        itemCount={visibleRows.length}
        itemSize={ROW_HEIGHT}
        overscanCount={10}
        itemData={{ visibleRows, folderList, newlyVisibleIds }}
      >
        {({ index, style, data }) => {
          const { folder, depth } = data.visibleRows[index];
          const isNew = data.newlyVisibleIds.has(folder.id);
          return (
            <Box
              key={folder.id}
              style={style}
              sx={
                isNew
                  ? {
                      "@keyframes folderSlideIn": {
                        from: { opacity: 0, transform: "translateY(-6px)" },
                        to: { opacity: 1, transform: "translateY(0)" }
                      },
                      animation: "folderSlideIn 500ms ease"
                    }
                  : undefined
              }
            >
              <Folder folder={folder} depth={depth} folderList={data.folderList} />
            </Box>
          );
        }}
      </List>
    </Stack>
  );
};
